import { getTypeLabelByName } from '@/lib/crm/utils/getTypeLabel';
import {
  type CoreMapping,
  type MappingAction,
  type MappingProposal,
  MappingReviewStep,
} from '@/lib/vendor-import';

export interface HubspotImportReviewStepProps {
  proposalsByEntity: Record<string, MappingProposal[]> | null;
  loadError: boolean;
  truncatedByEntity: Record<string, boolean> | null;
  /**
   * Per-entity-type flag: true when the backend's record scan hit its
   * sampling cap, so counts for that entity type are approximate rather
   * than exhaustive. null when proposals haven't loaded.
   */
  sampledByEntity: Record<string, boolean> | null;
  totalRecordsByEntity: Record<string, number> | null;
  coreMappingsByEntity: Record<string, CoreMapping[]> | null;
  actionByKey: Record<string, MappingAction>;
  onActionChange: (key: string, action: MappingAction) => void;
  confirmError: string | null;
}

export function HubspotImportReviewStep({
  proposalsByEntity,
  loadError,
  truncatedByEntity,
  sampledByEntity,
  totalRecordsByEntity,
  coreMappingsByEntity,
  actionByKey,
  onActionChange,
  confirmError,
}: HubspotImportReviewStepProps) {
  // Mirrors MappingReviewStep's own `sections` filter so the notice only
  // appears for entity-type groups that actually render a section there.
  const sampledEntityTypes = proposalsByEntity
    ? Object.entries(proposalsByEntity)
        .filter(
          ([entityType, proposals]) =>
            proposals.length > 0 && sampledByEntity?.[entityType]
        )
        .map(([entityType]) => entityType)
    : [];

  return (
    <div className='flex flex-col gap-4'>
      <MappingReviewStep
        proposalsByEntity={proposalsByEntity}
        loadError={loadError}
        truncatedByEntity={truncatedByEntity}
        totalRecordsByEntity={totalRecordsByEntity}
        coreMappingsByEntity={coreMappingsByEntity}
        actionByKey={actionByKey}
        onActionChange={onActionChange}
      />
      {sampledEntityTypes.length > 0 && (
        <div className='flex flex-col gap-1'>
          {sampledEntityTypes.map(entityType => (
            <p key={entityType} className='text-body-small text-tertiary'>
              Counts for{' '}
              {getTypeLabelByName(entityType, { plural: true }).toLowerCase()}{' '}
              are approximate — the record scan hit its cap.
            </p>
          ))}
        </div>
      )}
      {confirmError && (
        <p
          role='alert'
          className='text-body-small text-[var(--color-text-error-primary)]'
        >
          {confirmError}
        </p>
      )}
    </div>
  );
}
